<!--
    Native order-side cell for the ADR 0014 grid (hosted renderer), ported from the ag-hosted
    SideCell wrapper: shows the side value with an info icon on MULTI orders. The app-wide legs
    hover card activates on the `legs-icon-hover` class and reads query options from the
    element-keyed registry, so the icon works identically over the new grid.
-->
<script lang="ts">
    import { legsQueryOptionsRegistry } from './legsQueryOptionsRegistry';
    import type { LegsQueryOptions } from './types';
    import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
    import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
    import { signal } from '@preact/signals-react';
    import type { GridHostedCellProps } from '@thinkalpha/ui-table-svelte/svelteCellHost.svelte';
    import { getLegsForOrderQuery } from 'src/queries/legQueries';

    interface Props extends GridHostedCellProps {
        /** Static per-column props: optional to satisfy the host contract, always provided. */
        sideFieldId?: string;
        clOrdIdFieldId?: string;
    }

    let { cell, sideFieldId, clOrdIdFieldId }: Props = $props();

    const sideValue = $derived.by(() => {
        const value = sideFieldId === undefined ? undefined : cell.row[sideFieldId];
        return typeof value === 'string' ? value : String(value ?? '');
    });
    const isMultiLeg = $derived(sideValue.toUpperCase() === 'MULTI');

    // The hover-card provider reads a preact signal from the element registry; the effect keeps
    // it synced with the derived row state.
    const queryOptions = signal<LegsQueryOptions>(null);
    $effect(() => {
        const clOrdId = clOrdIdFieldId === undefined ? undefined : cell.row[clOrdIdFieldId];
        queryOptions.value =
            isMultiLeg && typeof clOrdId === 'string' && clOrdId !== '' ? getLegsForOrderQuery(clOrdId) : null;
    });

    /** Reference to the icon element, used as the key for the WeakMap registry */
    let iconElement: HTMLSpanElement | undefined = $state();
    $effect(() => {
        if (iconElement) {
            legsQueryOptionsRegistry.set(iconElement, queryOptions);
        }
    });
</script>

<span class="flex items-center">
    <span>{sideValue}</span>
    {#if isMultiLeg}
        <span
            bind:this={iconElement}
            class="legs-icon-hover ml-1 cursor-pointer text-steel-500 transition-colors hover:text-blue-500"
            role="button"
            tabindex="0"
            aria-label="View order legs"
        >
            <span aria-hidden="true"><FontAwesomeIcon icon={faCircleInfo} class="text-xs" /></span>
        </span>
    {/if}
</span>
